import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';
import { text } from './peopleCopy';

/*
 * Sub-services — layar `subservices` paket client.
 *
 * Kembar dari Services, jadi bentuk dan aturannya sengaja dibuat sama: kunci
 * disebutkan lebih dulu, harga ditampilkan sebagai rentang, dan penghapusan
 * menyebutkan berapa booking yang menunjuk baris ini.
 *
 * Kolom Price prototipe jadi rentang untuk alasan yang sama seperti di
 * Services: harga disimpan per lokasi, jadi satu sub-layanan bisa punya
 * delapan harga berbeda sekaligus.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String,
                  $categoryId: Int, $state: String) {
  getAdminSubServices(currentPage: $currentPage, limit: $limit, searchList: $searchList,
                      categoryId: $categoryId, state: $state) {
    count total activeCount status errorMessage
    results {
      id name description status categoryId parentName parentStatus currency
      activePricingCount priceMin priceMax bookingCount
      lockedOffReason lockedDeleteReason
    }
  }
}`;

const Q_CATS = `{ getActiveCategories { results { id name } } }`;

// addSubCategory menangani buat DAN sunting: id kosong berarti membuat.
const M_SAVE = `mutation ($id: Int, $name: String, $description: String,
                 $categoryId: Int, $status: String) {
  addSubCategory(id: $id, name: $name, description: $description,
                 categoryId: $categoryId, status: $status) { status errorMessage }
}`;

const M_STATUS = `mutation ($id: Int, $fieldName: String, $fieldValue: String) {
  updateSubCategoryStatus(id: $id, fieldName: $fieldName, fieldValue: $fieldValue) { status errorMessage }
}`;

const M_DELETE = `mutation ($id: Int) {
  deleteSubCategory(id: $id) { status errorMessage }
}`;

// Bentuk balasan mutation katalog tidak konsisten — sebagian mengembalikan
// status sebagai Int, sebagian sebagai String. Dibandingkan numerik supaya
// tindakan yang berhasil tidak pernah terbaca gagal.
const ok = res => res && Number(res.status) === 200;

const money = (n, c) => `${c || 'AED'} ${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

class SubServices extends React.Component {
  state = {
    rows: [], count: 0, total: 0, activeCount: 0, cats: [],
    page: 1, perPage: 25, search: '', categoryId: '', visibility: '',
    loading: true, error: null,
    form: null, formError: null, deleting: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); this.loadCats(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    categoryId: this.state.categoryId ? Number(this.state.categoryId) : undefined,
    state: this.state.visibility || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminSubServices;
    if (!ok(res)) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load sub-services.', 'تعذر تحميل الخدمات الفرعية.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0,
      total: res.total || 0, activeCount: res.activeCount || 0, loading: false
    });
  };

  loadCats = async () => {
    const json = await gql(Q_CATS).catch(() => null);
    const cats = json && json.data && json.data.getActiveCategories
      && json.data.getActiveCategories.results;
    if (cats && cats.length) this.setState({ cats });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  pick = key => e => this.setState({ [key]: e.target.value, page: 1 }, () => this.load());

  openAdd = () => this.setState({
    form: {
      id: null, name: '', description: '',
      categoryId: this.state.categoryId || (this.state.cats[0] || {}).id || '',
      status: 'active', lockedOffReason: null
    },
    formError: null, notice: null
  });

  openEdit = r => this.setState({
    form: {
      id: r.id, name: r.name, description: r.description || '',
      categoryId: r.categoryId, status: r.status,
      lockedOffReason: r.lockedOffReason
    },
    formError: null, notice: null
  });

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  submit = async () => {
    const f = this.state.form;
    if (!f.name.trim() || !f.categoryId) {
      this.setState({ formError: this.tx('A name and a parent service are both required.', 'الاسم والخدمة الرئيسية مطلوبان.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    const json = await gql(M_SAVE, {
      id: f.id || undefined,
      name: f.name.trim(),
      description: f.description || '',
      categoryId: Number(f.categoryId),
      status: f.status
    }).catch(() => null);
    const res = json && json.data && json.data.addSubCategory;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the sub-service.', 'تعذر حفظ الخدمة الفرعية.') });
      return;
    }
    this.setState({
      form: null,
      notice: { tone: 'info', text: f.id ? `${f.name.trim()} ${this.tx('updated.', 'تم تحديثها.')}` : `${f.name.trim()} ${this.tx('created.', 'تم إنشاؤها.')}` }
    });
    this.load(true);
  };

  toggleStatus = async r => {
    const next = r.status === 'active' ? 'inactive' : 'active';
    this.setState({ saving: true, notice: null });
    const json = await gql(M_STATUS,
      { id: r.id, fieldName: 'status', fieldValue: next }).catch(() => null);
    const res = json && json.data && json.data.updateSubCategoryStatus;
    this.setState({ saving: false });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not change the status.', 'تعذر تغيير الحالة.') }
        : {
          tone: 'info',
          text: next === 'active'
            ? `${r.name} ${this.tx('is selectable again under', 'متاحة للاختيار مرة أخرى ضمن')} ${r.parentName}.`
            : `${r.name} ${this.tx('no longer appears under', 'لم تعد تظهر ضمن')} ${r.parentName}. ${this.tx('Existing bookings are unaffected.', 'الحجوزات الحالية لا تتأثر.')}`
        }
    });
    this.load(true);
  };

  remove = async () => {
    const r = this.state.deleting;
    this.setState({ saving: true });
    const json = await gql(M_DELETE, { id: r.id }).catch(() => null);
    const res = json && json.data && json.data.deleteSubCategory;
    this.setState({ saving: false, deleting: null });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not delete the sub-service.', 'تعذر حذف الخدمة الفرعية.') }
        : { tone: 'danger', text: `${r.name} ${this.tx('was deleted.', 'تم حذفها.')}` }
    });
    this.load(true);
  };

  columns() {
    const t = this.tx;
    return [
      { key: 'name', label: t('Sub-service', 'الخدمة الفرعية'), sortable: true, render: r => <b>{r.name}</b> },
      {
        key: 'parentName', label: t('Parent service', 'الخدمة الرئيسية'), sortable: true,
        render: r => (
          <span>
            {r.parentName || <span className="muted">{t('Orphaned', 'بلا خدمة رئيسية')}</span>}
            {/* Sub-layanan aktif di bawah layanan yang disembunyikan tidak
                pernah muncul di aplikasi, dan tidak ada tanda apa pun soal itu
                di layar mana pun sebelum ini. */}
            {r.parentStatus === 'inactive' && (
              <span className="tag" style={{ marginInlineStart: 8 }}><i />{t('Parent hidden', 'الخدمة الرئيسية مخفية')}</span>
            )}
          </span>
        )
      },
      {
        key: 'priceMin', label: t('Price range', 'نطاق السعر'), align: 'end', sortable: true,
        sortValue: r => (r.priceMin === null ? -1 : r.priceMin),
        render: r => r.priceMin === null
          ? <span className="muted">{t('No prices set', 'لم يتم تحديد أسعار')}</span>
          : (
            <span className="num">
              {r.priceMin === r.priceMax
                ? money(r.priceMin, r.currency)
                : `${money(r.priceMin, r.currency)}–${Math.round(r.priceMax).toLocaleString('en-US')}`}
            </span>
          )
      },
      { key: 'bookingCount', label: t('Bookings', 'الحجوزات'), align: 'end', sortable: true },
      {
        key: 'status', label: t('Status', 'الحالة'),
        render: r => (
          <span className={`tag ${r.status === 'active' ? 'ok' : ''}`}>
            <i />{r.status === 'active' ? t('Bookable', 'متاح للحجز') : t('Hidden', 'مخفي')}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title="Edit sub-service"
              aria-label={`Edit ${r.name}`} onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
            <button className="btn icon" type="button"
              title={r.lockedOffReason || (r.status === 'active' ? 'Hide from the apps' : 'Make bookable')}
              aria-label={`Toggle ${r.name}`}
              disabled={!!r.lockedOffReason}
              onClick={() => this.toggleStatus(r)}>
              <Icon name={r.status === 'active' ? 'x' : 'check'} />
            </button>
            <button className="btn icon" type="button"
              title={r.lockedDeleteReason || 'Delete sub-service'}
              aria-label={`Delete ${r.name}`}
              disabled={!!r.lockedDeleteReason}
              onClick={() => this.setState({ deleting: r, notice: null })}>
              <Icon name="trash" />
            </button>
          </span>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError, cats } = this.state;
    const t = this.tx;

    return (
      <Modal
        icon="layers" wide
        title={f.id ? `${t('Edit', 'تعديل')} ${f.name}` : t('Add sub-service', 'إضافة خدمة فرعية')}
        sub={t('Prices are set per area on the Pricing screen, not here.', 'تُحدد الأسعار حسب المنطقة في شاشة التسعير، وليس هنا.')}
        confirmLabel={f.id ? t('Save sub-service', 'حفظ الخدمة الفرعية') : t('Create sub-service', 'إنشاء الخدمة الفرعية')}
        busy={saving}
        disabled={!f.name.trim() || !f.categoryId}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        <Fieldset title={t('Sub-service', 'الخدمة الفرعية')}>
          <Field label={t('Name', 'الاسم')} required>
            <input className="inp" value={f.name} placeholder="Flatbed towing"
              onChange={e => this.setForm('name', e.target.value)} />
          </Field>
          <Field label={t('Parent service', 'الخدمة الرئيسية')} required
            hint={f.id ? t('Moving it changes where customers find it.', 'نقلها يغير مكان ظهورها للعملاء.') : null}>
            <select className="inp" value={f.categoryId}
              onChange={e => this.setForm('categoryId', e.target.value)}>
              <option value="">{t('Choose a service…', 'اختر خدمة…')}</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label={t('Description', 'الوصف')} span>
            <textarea className="inp" rows={2} value={f.description}
              onChange={e => this.setForm('description', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Availability', 'التوفر')} rows>
          <SettingRow label={t('Selectable in the apps', 'متاحة للاختيار في التطبيقات')}
            hint={t('Hidden sub-services keep their history but stop appearing at booking time.', 'تحتفظ الخدمات الفرعية المخفية بسجلها ولا تظهر وقت الحجز.')}
            note={f.status === 'active' && f.lockedOffReason ? `${t('Locked', 'مقفل')}: ${f.lockedOffReason}` : null}>
            <Switch checked={f.status === 'active'} label={t('Selectable in the apps', 'متاحة للاختيار في التطبيقات')}
              disabled={f.status === 'active' && !!f.lockedOffReason}
              onChange={v => this.setForm('status', v ? 'active' : 'inactive')} />
          </SettingRow>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const {
      rows, count, total, activeCount, cats, page, perPage, loading, error,
      categoryId, visibility, form, deleting, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="subservices"
        breadcrumb={[t('Catalogue', 'الكتالوج'), t('Sub-services', 'الخدمات الفرعية')]}
        title={t('Sub-services', 'الخدمات الفرعية')}
        lead={this.locale() === 'ar'
          ? `${total} خياراً عبر جميع الخدمات، ${activeCount} متاح للحجز.`
          : `${total} option${total === 1 ? '' : 's'} across all services, ${activeCount} bookable.`}
        actions={[
          <a className="btn ghost" href="/siteadmin/sub-category" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="a" onClick={this.openAdd}>
            <Icon name="plus" />{t('Add sub-service', 'إضافة خدمة فرعية')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        {form && this.renderForm()}

        {deleting && (
          <Modal
            icon="trash" tone="danger"
            title={`${t('Delete', 'حذف')} ${deleting.name}؟`}
            sub={this.locale() === 'ar' ? `لن تُعرض بعد الآن ضمن ${deleting.parentName}.` : `It will no longer be offered under ${deleting.parentName}.`}
            confirmLabel={t('Delete sub-service', 'حذف الخدمة الفرعية')}
            busy={saving}
            onConfirm={this.remove}
            onClose={() => this.setState({ deleting: null })}
          >
            <Summary pairs={[
              [t('Parent service', 'الخدمة الرئيسية'), deleting.parentName || '—'],
              [t('Historical bookings', 'الحجوزات السابقة'), String(deleting.bookingCount)],
              [t('Active price rows', 'صفوف الأسعار النشطة'), String(deleting.activePricingCount)]
            ]} />
            {deleting.bookingCount > 0 && (
              <div className="notice danger" style={{ marginBottom: 0 }}>
                <span className="ni">!</span>
                <span className="nt">
                  {this.locale() === 'ar'
                    ? `هناك ${deleting.bookingCount} حجوزات سابقة لهذا الخيار. حذف الصف لا يحذفها، بل يترك سجلات تشير إلى خيار لم يعد موجوداً. أخفِه بدلاً من ذلك لإيقاف الحجوزات الجديدة دون فقدان السجل.`
                    : `${deleting.bookingCount} past booking${deleting.bookingCount === 1 ? '' : 's'} were placed for this option. Deleting the row does not delete them — it leaves those records naming an option that no longer exists. Hide it instead to stop new bookings without losing the history.`}
                </span>
              </div>
            )}
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search sub-service or parent service', 'ابحث عن الخدمة الفرعية أو الرئيسية')}
              filters={
                <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                  <select className="inp" value={categoryId} onChange={this.pick('categoryId')}
                    aria-label={t('Filter by parent service', 'تصفية حسب الخدمة الرئيسية')}>
                    <option value="">{t('Service: all', 'الخدمة: الكل')}</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select className="inp" value={visibility} onChange={this.pick('visibility')}
                    aria-label={t('Filter by status', 'تصفية حسب الحالة')}>
                    <option value="">{t('Status: all', 'الحالة: الكل')}</option>
                    <option value="active">{t('Bookable', 'متاح للحجز')}</option>
                    <option value="inactive">{t('Hidden', 'مخفي')}</option>
                  </select>
                </span>
              }
              emptyText={t('No sub-services match', 'لا توجد خدمات فرعية مطابقة')}
              emptyHint={t('Try clearing the service or status filter.', 'جرّب مسح تصفية الخدمة أو الحالة.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(SubServices);
